import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Phone, Mail, MapPin, ShieldCheck } from 'lucide-react'
import { getOptimizedImageUrl } from '../lib/imageOptimization.js'

export default function Footer() {
  const currentYear = new Date().getFullYear()
  const [settings, setSettings] = useState(null)

  useEffect(() => {
    let isMounted = true
    const loadSettings = async () => {
      try {
        const res = await fetch('/_api/public/content/generelle_indstillinger')
        if (!res.ok) return
        const data = await res.json()
        const items = Array.isArray(data) ? data : (data.items || [])
        if (items.length > 0 && isMounted) {
          setSettings(items[0])
        }
      } catch (e) {
        // Fallback
      }
    }
    loadSettings()
    return () => { isMounted = false }
  }, [])

  const quickLinks = [
    { label: 'Forside', href: '/' },
    { label: 'Se vores arbejde', href: '/referencer' },
    { label: 'Kontakt os', href: '/kontakt' },
  ]

  const serviceLinks = [
    { label: 'Tagarbejde', href: '/tagarbejde' },
    { label: 'Renovering af hus', href: '/renovering' },
    { label: 'Udskiftning af vinduer og døre', href: '/vinduer-og-doere' },
    { label: 'Træterasse', href: '/traeterasse' },
    { label: 'Gulve og lofte', href: '/gulve-og-lofter' },
    { label: 'Hegn og stakit', href: '/hegn-og-stakit' },
  ]

  return (
    <footer className="bg-[#1e1e1e] text-stone-300 border-t border-white/10">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Company Info */}
          <div className="space-y-4">
            <img
              src={getOptimizedImageUrl(
                settings?.logo?.[0] || 'https://assets.auxodev.dk/assets/cf6a3489-9db0-408d-9584-8086b110e180/727ae610-4996-4e0c-beb1-82ff74ac0cde.png',
                { width: 300, quality: 90 }
              )}
              alt={settings?.firmanavn || "Tømrermester Daniel Vandet"}
              className="h-12 w-auto object-contain"
              loading="lazy"
              decoding="async"
            />
            <p className="text-sm text-stone-300/80 leading-relaxed">
              Professionel tømrermester med fokus på solidt håndværk, renovering, nybyg, træarbejde og løsninger til private og erhverv.
            </p>
            <div className="flex items-center gap-2 text-xs text-stone-300 bg-white/5 p-3 rounded-sm border border-white/10 w-fit">
              <ShieldCheck className="h-4 w-4 text-[#c08f66] shrink-0" />
              <span>CVR-nr: {settings?.cvr || '40509496'}</span>
            </div>
          </div>

          {/* Contact Details */}
          <div className="space-y-4">
            <h4 className="text-white text-sm font-semibold uppercase tracking-wider">
              Kontaktoplysninger
            </h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-[#c08f66] shrink-0 mt-0.5" />
                <span className="text-stone-300 whitespace-pre-line">
                  {settings?.adresse || 'Lyngvænget 73\n7870 Glyngøre, Denmark'}
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-[#c08f66] shrink-0" />
                <a href={`tel:${(settings?.telefon || '+45 20 53 27 67').replace(/\s+/g, '')}`} className="text-stone-300 hover:text-white transition-colors whitespace-nowrap font-medium">
                  {settings?.telefon || '+45 20 53 27 67'}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-[#c08f66] shrink-0" />
                <a href={`mailto:${settings?.email || 'dvbolig@hotmail.com'}`} className="text-stone-300 hover:text-white transition-colors whitespace-nowrap font-medium">
                  {settings?.email || 'dvbolig@hotmail.com'}
                </a>
              </li>
            </ul>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="text-white text-sm font-semibold uppercase tracking-wider">
              Hurtige links
            </h4>
            <ul className="space-y-2 text-sm">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="hover:text-white hover:underline transition-all">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Links */}
          <div className="space-y-4">
            <h4 className="text-white text-sm font-semibold uppercase tracking-wider">
              Vores specialer
            </h4>
            <ul className="space-y-2 text-sm">
              {serviceLinks.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="hover:text-white hover:underline transition-all">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-[#121212] text-stone-400 py-6 px-4 sm:px-6 lg:px-8 text-xs border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <p>© {currentYear} Tømrermester Daniel Vandet. Alle rettigheder forbeholdes.</p>
          <p className="flex items-center gap-1">
            <span>Udviklet med fokus på kvalitet og holdbarhed</span>
          </p>
        </div>
      </div>
    </footer>
  )
}
